import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Lightbulb, FileText, Ruler, Users, CheckCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Design = () => {
  const designServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://greenfrogutilities.co.uk/services/design#service",
    "name": "Utility Infrastructure Design",
    "serviceType": "Utility design and engineering",
    "description": "Design and engineering of utility infrastructure for commercial and industrial developments, covering feasibility, network design and detailed construction drawings.",
    "provider": { "@id": "https://greenfrogutilities.co.uk/#organization" },
    "areaServed": { "@type": "Country", "name": "United Kingdom" }
  };

  const designBreadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://greenfrogutilities.co.uk/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://greenfrogutilities.co.uk/services/connections" },
      { "@type": "ListItem", "position": 3, "name": "Utility Infrastructure Design", "item": "https://greenfrogutilities.co.uk/services/design" }
    ]
  };

  const features = [
    {
      icon: FileText,
      title: 'Comprehensive Planning',
      description: 'Detailed project planning and feasibility studies to ensure your project starts on the right foot'
    },
    {
      icon: Ruler,
      title: 'Technical Design',
      description: 'Expert engineering design using the latest software and best practices for optimal performance'
    },
    {
      icon: Users,
      title: 'Collaborative Approach',
      description: 'Working closely with stakeholders to deliver designs that meet all requirements and regulations'
    },
    {
      icon: Lightbulb,
      title: 'Innovative Solutions',
      description: 'Creative problem-solving to overcome challenges and deliver cost-effective solutions'
    }
  ];

  const services = [
    'Feasibility studies and site assessments',
    'Detailed engineering design for gas and electrical infrastructure',
    'CAD drawings and technical specifications',
    'Regulatory compliance and approvals',
    'Cost estimation and budget planning',
    'Design optimization for efficiency and sustainability',
    'Coordination with utility companies and authorities',
    'Health and safety planning'
  ];

  return (
    <div>
      <SEO 
        title="Gas Infrastructure Design | Utility Design UK | Green Frog Utilities"
        description="Commercial gas infrastructure services and gas meter capacity sizing UK. Gas meter installation standards UK, rotary gas meters installation, turbine gas meters commercial, diaphragm gas meters, ultrasonic gas flow meters industrial."
        keywords="commercial gas infrastructure services, gas meter capacity sizing UK, gas meter installation standards UK, rotary gas meters installation, turbine gas meters commercial, diaphragm gas meters commercial, ultrasonic gas flow meters industrial, gas meter pressure regulating systems"
        canonical="/services/design"
        jsonLd={[designServiceSchema, designBreadcrumbSchema]}
      />
      {/* Hero Section */}
      <section className="relative h-[500px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/ps80hbg4_144.%20Photo%201%20%2025.09.19%20%282%29.jpg)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/40 to-emerald-700/20" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl text-white">
            <div className="flex items-center gap-3 mb-4">
              <Lightbulb className="h-12 w-12" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
                Gas Infrastructure Design
              </h1>
            </div>
            <p className="text-xl text-white font-bold">
              Commercial gas infrastructure services and gas meter capacity sizing UK. Our solutions are driven by experienced personnel, adopting the latest design and engineering practices.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Expert Utilities Design
              </h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Our design team brings together extensive experience in utilities infrastructure planning and engineering. We use the latest design software and methodologies to create efficient, cost-effective solutions tailored to your specific requirements.
                </p>
                <p>
                  From initial feasibility studies through to detailed engineering drawings, we handle every aspect of the design process. Our designs comply with all relevant regulations and standards, ensuring smooth approval processes and successful project delivery.
                </p>
                <p>
                  Whether you're developing a new site or upgrading existing infrastructure, our design services provide the solid foundation your project needs.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/contact">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    Request a Design Consultation
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-8 rounded-lg flex items-center justify-center">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <FileText className="h-7 w-7 text-white" />
                    </div>
                    <div className="h-1 w-16 bg-emerald-600 rounded"></div>
                    <div className="w-14 h-14 bg-emerald-500 rounded-lg flex items-center justify-center">
                      <Ruler className="h-7 w-7 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-400 rounded-lg flex items-center justify-center">
                      <Users className="h-7 w-7 text-white" />
                    </div>
                    <div className="h-1 w-16 bg-emerald-400 rounded"></div>
                    <div className="w-14 h-14 bg-emerald-300 rounded-lg flex items-center justify-center">
                      <Lightbulb className="h-7 w-7 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Design Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine technical excellence with practical experience to deliver designs that work
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-none hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 inline-flex p-4 bg-emerald-50 rounded-full">
                      <Icon className="h-10 w-10 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Design Services Include
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We offer a comprehensive range of design services covering all aspects of utilities infrastructure planning and engineering.
              </p>
              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://greenfrogutilities.co.uk/wp-content/uploads/2020/11/alcoa-stacks-green-frog-utilities-resized--scaled.jpg"
                alt="Utilities Design"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Design Process
            </h2>
            <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
              A structured approach that ensures quality results
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Initial Consultation', desc: 'Understanding your requirements and project scope' },
              { step: '02', title: 'Site Assessment', desc: 'Detailed surveys and feasibility studies' },
              { step: '03', title: 'Design Development', desc: 'Creating detailed engineering designs and specifications' },
              { step: '04', title: 'Approval & Handover', desc: 'Securing approvals and delivering final designs' }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-emerald-300 mb-4">{phase.step}</div>
                <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                <p className="text-emerald-100">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to start your design project?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and see how our design services can benefit your project
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Get a Quote
                </Button>
              </Link>
              <Link to="/services/connections">
                <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                  View Connections Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Design;
